import Keycloak, { KeycloakConfig, KeycloakInitOptions } from 'keycloak-js'
import { store } from '../store'
import {
  setKeycloakInstance,
  loginSuccess,
  loginFailure,
  setLoading,
  logout,
  refreshTokenSuccess,
} from '../store/slices/authSlice'
import type { User } from '../store/slices/authSlice'
import { loadMFAState } from './mfa'

// Keycloak configuration
const keycloakConfig: KeycloakConfig = {
  url: import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8180',
  realm: import.meta.env.VITE_KEYCLOAK_REALM || 'sovereignscope',
  clientId:
    import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'sovereignscope-frontend',
}

// Keycloak initialization options
const keycloakInitOptions: KeycloakInitOptions = {
  onLoad: 'check-sso', // Always use check-sso to avoid automatic redirects
  checkLoginIframe: false, // Disable iframe check to avoid frame issues
  enableLogging: import.meta.env.DEV,
  pkceMethod: 'S256', // Use PKCE for better security
  flow: 'standard', // Use standard flow
}

// Global Keycloak instance
let keycloakInstance: Keycloak | null = null

/**
 * Initialize Keycloak authentication
 */
export const initializeAuth = async (): Promise<boolean> => {
  try {
    store.dispatch(setLoading(true))

    // In production, if Keycloak URL is not configured, skip initialization
    if (import.meta.env.PROD && (!import.meta.env.VITE_KEYCLOAK_URL || (keycloakConfig.url && keycloakConfig.url.includes('localhost')))) {
      console.warn('Keycloak URL not configured for production, skipping auth initialization')
      store.dispatch(setLoading(false))
      return false
    }

    // Create Keycloak instance
    keycloakInstance = new Keycloak(keycloakConfig)
    store.dispatch(setKeycloakInstance(keycloakInstance))

    console.log('Initializing Keycloak with config:', keycloakConfig)

    // Initialize Keycloak with shorter timeout for better UX
    // Use check-sso to avoid redirects
    const authenticated = await Promise.race([
      keycloakInstance.init({
        ...keycloakInitOptions,
        onLoad: 'check-sso', // Never redirect, only check existing session
        checkLoginIframe: false, // Disable iframe to prevent redirects
      }),
      new Promise<boolean>((_, reject) => 
        setTimeout(() => reject(new Error('Keycloak initialization timeout')), 5000)
      )
    ])

    console.log('Keycloak initialization result:', { authenticated, token: !!keycloakInstance.token })

    if (authenticated && keycloakInstance.token) {
      console.log('User is authenticated, setting up session')
      
      // Extract user information from token
      const user = extractUserFromToken(keycloakInstance)

      store.dispatch(
        loginSuccess({
          token: keycloakInstance.token,
          refreshToken: keycloakInstance.refreshToken || '',
          user,
        })
      )

      console.log('Authentication state set for user:', user.username)

      // Load MFA state after successful authentication
      try {
        await loadMFAState()
      } catch (error) {
        console.warn('Failed to load MFA state:', error)
        // Don't fail authentication if MFA state loading fails
      }

      // Set up token refresh
      setupTokenRefresh()
    } else {
      console.log('User is not authenticated')
      // Don't set loading false here - let the calling component handle it
    }

    // Set up event listeners
    setupKeycloakEventListeners()

    return authenticated
  } catch (error) {
    console.error('Failed to initialize Keycloak:', error)
    
    // In production, fail gracefully without redirecting
    if (import.meta.env.PROD) {
      console.warn('Keycloak initialization failed in production - continuing without authentication')
      store.dispatch(setLoading(false))
      return false
    }
    
    // In development, provide a more graceful fallback
    if (import.meta.env.DEV) {
      console.warn('Keycloak initialization failed in development mode - continuing without authentication')
      console.warn('Make sure Keycloak is running on http://localhost:8083')
      console.warn('You can still use the application, but authentication features will be limited')
      store.dispatch(setLoading(false))
      return false
    }
    
    store.dispatch(loginFailure('Failed to initialize authentication'))
    store.dispatch(setLoading(false))
    return false // Don't throw, just return false to prevent redirects
  }
}

/**
 * Extract user information from Keycloak token
 */
const extractUserFromToken = (kc: Keycloak): User => {
  const tokenParsed = kc.tokenParsed
  if (!tokenParsed) {
    throw new Error('No token parsed')
  }

  return {
    id: tokenParsed.sub || '',
    email: tokenParsed['email'] || '',
    firstName: tokenParsed['given_name'],
    lastName: tokenParsed['family_name'],
    username: tokenParsed['preferred_username'] || tokenParsed['email'] || '',
    roles: tokenParsed.realm_access?.roles || [],
    organizations: tokenParsed['organizations'] || [],
    preferences: {
      theme: 'light',
      language: tokenParsed['locale'] || 'en',
    },
  }
}

/**
 * Set up automatic token refresh
 */
const setupTokenRefresh = (): void => {
  if (!keycloakInstance) return

  keycloakInstance.onTokenExpired = () => {
    refreshToken()
  }

  // Refresh token if it expires in less than 30 seconds
  setInterval(() => {
    if (keycloakInstance?.token) {
      keycloakInstance
        .updateToken(30)
        .then(refreshed => {
          if (
            refreshed &&
            keycloakInstance?.token &&
            keycloakInstance?.refreshToken
          ) {
            store.dispatch(
              refreshTokenSuccess({
                token: keycloakInstance.token,
                refreshToken: keycloakInstance.refreshToken,
              })
            )
          }
        })
        .catch(error => {
          console.error('Failed to refresh token:', error)
          handleLogout()
        })
    }
  }, 60000) // Check every minute
}

/**
 * Set up Keycloak event listeners
 */
const setupKeycloakEventListeners = (): void => {
  if (!keycloakInstance) return

  keycloakInstance.onAuthSuccess = () => {
    console.log('Authentication successful')
  }

  keycloakInstance.onAuthError = error => {
    console.error('Authentication error:', error)
    store.dispatch(loginFailure('Authentication failed'))
  }

  keycloakInstance.onAuthLogout = () => {
    console.log('User logged out')
    store.dispatch(logout())
  }

  keycloakInstance.onTokenExpired = () => {
    console.log('Token expired, attempting refresh')
    refreshToken()
  }
}

/**
 * Login user
 */
export const login = async (): Promise<void> => {
  if (!keycloakInstance) {
    throw new Error('Keycloak not initialized')
  }

  try {
    await keycloakInstance.login({
      redirectUri: window.location.origin,
    })
  } catch (error) {
    console.error('Login failed:', error)
    store.dispatch(loginFailure('Login failed'))
  }
}

/**
 * Logout user
 */
export const handleLogout = async (): Promise<void> => {
  if (!keycloakInstance) {
    store.dispatch(logout())
    return
  }

  try {
    await keycloakInstance.logout({
      redirectUri: window.location.origin,
    })
    store.dispatch(logout())
  } catch (error) {
    console.error('Logout failed:', error)
    // Still clear local state even if Keycloak logout fails
    store.dispatch(logout())
  }
}

/**
 * Refresh authentication token
 */
export const refreshToken = async (): Promise<boolean> => {
  if (!keycloakInstance) {
    return false
  }

  try {
    const refreshed = await keycloakInstance.updateToken(5)

    if (refreshed && keycloakInstance.token && keycloakInstance.refreshToken) {
      store.dispatch(
        refreshTokenSuccess({
          token: keycloakInstance.token,
          refreshToken: keycloakInstance.refreshToken,
        })
      )
      return true
    }

    return false
  } catch (error) {
    console.error('Token refresh failed:', error)
    handleLogout()
    return false
  }
}

/**
 * Get current authentication token
 */
export const getToken = (): string | undefined => {
  return keycloakInstance?.token
}

/**
 * Check if user has specific role
 */
export const hasRole = (role: string): boolean => {
  if (!keycloakInstance?.tokenParsed) {
    return false
  }

  const realmRoles = keycloakInstance.tokenParsed.realm_access?.roles || []
  return realmRoles.includes(role)
}

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (roles: string[]): boolean => {
  return roles.some(role => hasRole(role))
}

/**
 * Get user profile from Keycloak
 */
export const getUserProfile = async (): Promise<Keycloak.KeycloakProfile> => {
  if (!keycloakInstance?.authenticated) {
    throw new Error('User not authenticated')
  }

  try {
    return await keycloakInstance.loadUserProfile()
  } catch (error) {
    console.error('Failed to load user profile:', error)
    throw error
  }
}

/**
 * Update user profile
 */
export const updateUserProfile = async (
  profile: Partial<Keycloak.KeycloakProfile>
): Promise<void> => {
  if (!keycloakInstance?.authenticated) {
    throw new Error('User not authenticated')
  }

  // This would typically make an API call to update the profile
  // Implementation depends on your backend API
  console.log('Update user profile:', profile)
  // TODO: Implement API call to update user profile
}

/**
 * Get Keycloak instance
 */
export const getKeycloakInstance = (): Keycloak | null => {
  return keycloakInstance
}

// Export default auth service object
export const authService = {
  initialize: initializeAuth,
  login,
  logout: handleLogout,
  refreshToken,
  getToken,
  hasRole,
  hasAnyRole,
  getUserProfile,
  updateUserProfile,
  getKeycloakInstance,
}
